import { useEffect, useState } from 'react';
import type {
  BankColumnMappingDto,
  DetectionResponseDto,
  LockedPeriodImportCandidateDto,
} from '@signalvote/shared';
import { BankFileFormat, DetectedFileKind, ErrorCode, SignConvention } from '@signalvote/shared';
import { FileTypeDetectorPanel } from '@/components/finance/detector';
import {
  BankIngestErrorWithCode,
  useCommitBankIngest,
  usePreviewBankMapping,
} from '@/hooks/queries/useBankIngestion';
import { fileToBase64 } from '@/hooks/queries/useFileTypeDetector';
import { LockedPeriodSkipPanel, lockedIncludeHint } from '@/components/finance/import';
import { BankAccountPicker, NO_ACCOUNT_BUCKET } from './BankAccountPicker';
import { BankColumnMappingPanel } from './BankColumnMappingPanel';

/**
 * ADR-015 D11 — one file's import pipeline, the per-file unit the multi-file
 * front door (<code>MultiFileImportFlow</code>) drives N of. The detector
 * pipeline (detect → pick → preview → commit) runs via
 * the existing <code>FileTypeDetectorPanel</code> seeded with this row's file.
 *
 * The bank-account gate is INVERTED from the old up-front picker (design §10.3):
 * files drop first and detect, and only a row detected as a bank statement asks
 * for an account — inline, after detection. A processor / donations / expenses
 * file never sees the account picker; its detector pipeline lands it in the
 * inbox on its own.
 *
 * Bank files DEFER their ImportBatch to this account step: the detector creates
 * no batch for them (so a resolved-but-never-committed upload leaves no orphan
 * that would falsely trip "already imported"). The bank-ingest commit below is
 * the batch-creating site — it posts the file bytes, not a batch id.
 */
interface FileImportRowProps {
  file: File;
  /**
   * Called whenever a commit for this row lands (detector pipeline OR the
   * bank-ingest commit) so the front door can refresh the import-history list.
   */
  onCommitted?: () => void;
  /** Remove this file's row from the multi-file batch. */
  onRemove?: () => void;
}

export function FileImportRow({ file, onCommitted, onRemove }: FileImportRowProps) {
  const [detection, setDetection] = useState<DetectionResponseDto | null>(null);
  const [bankAccountId, setBankAccountId] = useState<string | null>(null);
  const [needsMapping, setNeedsMapping] = useState(false);
  const [bankCommittedCount, setBankCommittedCount] = useState<number | null>(null);
  // Rows a commit dropped because their date is in an already-filed (locked)
  // period. Surfaced as a calm heads-up (never blocks) — both the detector and
  // the bank commit paths feed it.
  const [skippedCount, setSkippedCount] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Commit-time duplicate → soft-amber heads-up (not the red error surface).
  const [duplicateMessage, setDuplicateMessage] = useState<string | null>(null);
  // True when the row reached the bank/list path via the "Not sure / it's a mix"
  // pick (not a detected bank statement). The rows still route through BankCsv,
  // but a mix may not belong to a real account — so the copy stays neutral.
  const [mixedSource, setMixedSource] = useState(false);
  // Set when the treasurer clicked "Choose a different type" from the bank
  // account step: re-show the detector straight on the picker for the SAME file.
  const [startDetectorInPicker, setStartDetectorInPicker] = useState(false);
  // The file's bytes, base64'd once so the deferred bank commit + the
  // mapping-panel preview can post them (bank batches are created at commit now).
  const [fileBytesBase64, setFileBytesBase64] = useState<string | null>(null);
  // Profiled fast-path (KNOWN bank) locked-period review. `null` = not yet probed
  // (or the file had none and committed straight through); a non-empty array =
  // the probe found already-filed rows, so the review panel is shown and the
  // commit is HELD until the treasurer confirms. `profiledPreviewSha` binds the
  // eventual commit's force-includes to the exact previewed bytes (the sha-guard).
  const [profiledLocked, setProfiledLocked] = useState<LockedPeriodImportCandidateDto[] | null>(null);
  const [profiledSelectedLocked, setProfiledSelectedLocked] = useState<number[]>([]);
  const [profiledPreviewSha, setProfiledPreviewSha] = useState<string | null>(null);

  const { mutateAsync: commitBank, isPending: committing } = useCommitBankIngest();
  const { mutateAsync: previewBank, isPending: previewingBank } = usePreviewBankMapping();

  useEffect(() => {
    let cancelled = false;
    void fileToBase64(file).then((b) => {
      if (!cancelled) setFileBytesBase64(b);
    });
    return () => {
      cancelled = true;
    };
  }, [file]);

  const isBank = detection ? isBankKind(detection.kind) : false;
  const profileFormat = detection ? kindToFormat(detection.kind) : BankFileFormat.Csv;

  const handleDetectorImported = (
    _importBatchId: string | null,
    det?: DetectionResponseDto | null,
    meta?: { mixedSource?: boolean; skippedRowCount?: number },
  ) => {
    setDetection(det ?? null);
    setMixedSource(meta?.mixedSource ?? false);
    // Non-bank commits report their skipped count here; bank kinds defer (their
    // count arrives with the bank-ingest commit below).
    setSkippedCount(meta?.skippedRowCount ?? null);
    if (!det || !isBankKind(det.kind)) {
      // Non-bank rows are fully landed by the detector pipeline (preview →
      // commit → Pending inbox rows). Refresh the history now.
      onCommitted?.();
      return;
    }
    // Bank file: the detector deferred (no batch yet). Its rows attach once the
    // treasurer picks an account (and maps columns if no profile matched). The
    // history refresh waits for the bank-ingest commit, below.
    if (!det.bankSourceProfileId) {
      setNeedsMapping(true);
    }
  };

  // The bytes are pre-computed on mount; fall back to a fresh read so a click
  // that races the effect still commits.
  const resolveBytes = async () => fileBytesBase64 ?? (await fileToBase64(file));

  const handleCommitError = (e: unknown) => {
    if (e instanceof BankIngestErrorWithCode && e.code === ErrorCode.BankIngestDuplicateUpload) {
      // "Already imported" is a normal re-upload, not a failure — surface the
      // server's verbatim campaign-local copy as a soft heads-up.
      setDuplicateMessage(e.message);
      return;
    }
    setError(formatError(e));
  };

  // The actual profiled commit (known bank). Threads the treasurer's
  // force-includes + the previewed file sha (the sha-guard) when present. Callers
  // guarantee bankAccountId is set before invoking.
  const runProfiledCommit = async (includeLockedRowIndices?: number[]) => {
    const result = await commitBank({
      fileBytesBase64: await resolveBytes(),
      fileName: file.name,
      // The detector-matched profile (known bank) fast-paths the commit.
      ...(detection?.bankSourceProfileId
        ? { bankSourceProfileId: detection.bankSourceProfileId }
        : {}),
      ...accountOrBucket(bankAccountId!),
      ...(includeLockedRowIndices && includeLockedRowIndices.length > 0
        ? { includeLockedRowIndices }
        : {}),
      // Bind the force-includes to the exact bytes the probe read.
      ...(profiledPreviewSha ? { previewFileSha256: profiledPreviewSha } : {}),
    });
    setBankCommittedCount(result.importedCount);
    setSkippedCount(result.skippedRowCount ?? null);
    onCommitted?.();
  };

  // Profiled fast-path: PROBE the file for already-filed rows BEFORE committing.
  // No candidates ⇒ commit straight through (zero added friction, as before).
  // Candidates ⇒ hold the commit and render the review panel so the treasurer can
  // include any before it lands — the recurring year-to-date case where a 2nd+
  // import from a saved bank overlaps periods already filed. Never
  // commit-then-restore (that would risk duplicating the non-locked rows).
  const commitProfiledBank = async () => {
    if (!bankAccountId) {
      setError('Pick a bank account to import these rows.');
      return;
    }
    setError(null);
    setDuplicateMessage(null);
    try {
      const previewResult = await previewBank({
        fileBytesBase64: await resolveBytes(),
        fileName: file.name,
        // The known bank drives the parse server-side; sign/format below are
        // ignored when a profile id is present (sent to satisfy the request type).
        ...(detection?.bankSourceProfileId
          ? { bankSourceProfileId: detection.bankSourceProfileId }
          : {}),
        signConvention: SignConvention.SignedAmount,
        profileFormat,
      });
      setProfiledPreviewSha(previewResult.previewFileSha256 ?? null);
      const candidates = previewResult.lockedPeriodCandidates ?? [];
      if (candidates.length === 0) {
        await runProfiledCommit();
        return;
      }
      // Locked rows present — show the review panel; confirmProfiledBank commits.
      setProfiledLocked(candidates);
    } catch (e) {
      handleCommitError(e);
    }
  };

  // Confirm the profiled review: commit with the treasurer's force-includes,
  // ordered by the candidate list (not click order) and filtered to the current
  // set so the wire is deterministic and never stale.
  const confirmProfiledBank = async () => {
    if (!bankAccountId) return;
    setError(null);
    setDuplicateMessage(null);
    const ordered = (profiledLocked ?? [])
      .filter((c) => profiledSelectedLocked.includes(c.rowIndex))
      .map((c) => c.rowIndex);
    try {
      await runProfiledCommit(ordered);
    } catch (e) {
      handleCommitError(e);
    }
  };

  const toggleProfiledLocked = (rowIndex: number) =>
    setProfiledSelectedLocked((prev) =>
      prev.includes(rowIndex) ? prev.filter((i) => i !== rowIndex) : [...prev, rowIndex],
    );

  const toggleAllProfiledLocked = () => {
    const all = (profiledLocked ?? []).map((c) => c.rowIndex);
    setProfiledSelectedLocked((prev) => (all.every((i) => prev.includes(i)) ? [] : all));
  };

  const handleConfirmMapping = async (input: {
    mapping: BankColumnMappingDto;
    dateFormat: string;
    signConvention: SignConvention;
    includeLockedRowIndices?: number[];
    previewFileSha256?: string;
  }) => {
    if (!bankAccountId) return;
    setError(null);
    setDuplicateMessage(null);
    try {
      // No `newProfileBankIdentifier` — the server derives the saved profile
      // label from the picked account's display name (the double bank-name is
      // collapsed). The wire field stays an optional override the UI no longer
      // sends.
      const result = await commitBank({
        fileBytesBase64: await resolveBytes(),
        fileName: file.name,
        ...accountOrBucket(bankAccountId),
        mapping: input.mapping,
        dateFormat: input.dateFormat,
        signConvention: input.signConvention,
        profileFormat,
        // The treasurer's force-includes for already-filed rows (omitted ⇒ the
        // server skips every locked row, the default).
        ...(input.includeLockedRowIndices && input.includeLockedRowIndices.length > 0
          ? { includeLockedRowIndices: input.includeLockedRowIndices }
          : {}),
        // Bind those force-includes to the previewed bytes (the sha-guard).
        ...(input.previewFileSha256 ? { previewFileSha256: input.previewFileSha256 } : {}),
      });
      setBankCommittedCount(result.importedCount);
      setSkippedCount(result.skippedRowCount ?? null);
      setNeedsMapping(false);
      onCommitted?.();
    } catch (e) {
      handleCommitError(e);
    }
  };

  // FIX 3: re-pick the kind for the SAME file from the bank account step. No
  // batch exists yet (deferred), so going back is clean — reset bank state and
  // re-show the detector straight on the picker.
  const handleChooseDifferentType = () => {
    setDetection(null);
    setBankAccountId(null);
    setNeedsMapping(false);
    setMixedSource(false);
    setSkippedCount(null);
    setError(null);
    setDuplicateMessage(null);
    setProfiledLocked(null);
    setProfiledSelectedLocked([]);
    setProfiledPreviewSha(null);
    setStartDetectorInPicker(true);
  };

  const fileNameHeader = (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <span className="text-sm font-semibold text-foreground break-all">{file.name}</span>
      {onRemove && bankCommittedCount === null && (
        <button
          type="button"
          onClick={onRemove}
          className="min-h-11 rounded-md px-2 py-1 text-xs font-medium text-muted-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          Remove
        </button>
      )}
    </div>
  );

  // Calm, non-blocking heads-up when a commit dropped rows dated in an
  // already-filed period. Muted (not red) — the rows aren't lost, just not
  // re-imported; the treasurer can re-add any by hand. Shared by both the
  // bank-commit success view and the detector-pipeline view below.
  const skippedNotice =
    skippedCount !== null && skippedCount > 0 ? (
      <p role="status" className="text-sm text-muted-foreground">
        {skippedImportCopy(skippedCount)}
      </p>
    ) : null;

  if (bankCommittedCount !== null) {
    return (
      <div className="rounded-lg border border-border p-4 space-y-2">
        {fileNameHeader}
        <p className="text-sm">
          {bankCommittedCount > 0 ? (
            <>
              Imported <strong>{bankCommittedCount}</strong> bank rows — they&apos;re
              in the inbox.
            </>
          ) : (
            <>File received. The inbox has a new entry.</>
          )}
        </p>
        {skippedNotice}
      </div>
    );
  }

  // Bank file resolved → collect the account, then commit (profiled) or
  // map-and-commit (no profile matched). The account picker is inline here, only
  // for bank rows (design §10.3 inversion). No ImportBatch exists yet — the
  // commit below creates it.
  if (isBank && detection) {
    return (
      <div className="rounded-lg border border-border p-4 space-y-4">
        {fileNameHeader}
        <p className="text-sm text-muted-foreground">
          {mixedSource
            ? 'Where should these transactions go?'
            : 'This looks like a bank statement — which account is it for?'}
        </p>
        <BankAccountPicker
          selectedAccountId={bankAccountId}
          onSelect={setBankAccountId}
          mixedSource={mixedSource}
        />

        {/* Already-imported heads-up: soft amber (never red). The server owns the
            hard block; this informs rather than alarms. Verbatim campaign-local
            copy. */}
        {duplicateMessage && (
          <div
            role="status"
            className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 space-y-1"
          >
            <p>{duplicateMessage}</p>
          </div>
        )}

        {error && (
          <p role="alert" className="text-sm text-red-700">
            {error}
          </p>
        )}

        {/* Profiled fast-path, not yet probed: one click probes for already-filed
            rows, then either commits straight through or reveals the panel below. */}
        {!needsMapping && profiledLocked === null && (
          <button
            type="button"
            onClick={commitProfiledBank}
            disabled={!bankAccountId || committing || previewingBank}
            className="min-h-11 rounded-md bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            {previewingBank ? 'Checking…' : committing ? 'Importing…' : 'Import to this account'}
          </button>
        )}

        {/* Probe found rows dated in an already-filed period on this KNOWN-bank
            re-import: skipped by default, force-include any/all, then confirm.
            The recurring year-to-date case that used to skip silently. */}
        {!needsMapping && profiledLocked !== null && profiledLocked.length > 0 && (
          <div className="space-y-4">
            <LockedPeriodSkipPanel
              candidates={profiledLocked}
              selected={profiledSelectedLocked}
              onToggle={toggleProfiledLocked}
              onToggleAll={toggleAllProfiledLocked}
            />
            <button
              type="button"
              onClick={confirmProfiledBank}
              disabled={!bankAccountId || committing}
              className="min-h-11 rounded-md bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              {committing
                ? 'Importing…'
                : `Import to this account${lockedIncludeHint(profiledSelectedLocked.length)}`}
            </button>
          </div>
        )}

        {needsMapping && bankAccountId && fileBytesBase64 && (
          <BankColumnMappingPanel
            fileBytesBase64={fileBytesBase64}
            fileName={file.name}
            headers={detection.headers}
            sampleRows={detection.sampleRows}
            profileFormat={profileFormat}
            onConfirm={handleConfirmMapping}
            onCancel={onRemove ?? (() => {})}
            busy={committing}
          />
        )}

        {/* FIX 3: this isn't a bank file after all — go back to the kind picker
            for the same file (nothing was committed, so it's a clean reset). */}
        <div>
          <button
            type="button"
            onClick={handleChooseDifferentType}
            disabled={committing}
            className="min-h-11 rounded-md px-2 py-1 text-xs font-medium text-muted-foreground underline-offset-2 hover:bg-muted hover:underline disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            Choose a different type
          </button>
        </div>
      </div>
    );
  }

  // Non-bank, or pre-detection: run the existing single-file detector pipeline
  // seeded with this row's file.
  return (
    <div className="rounded-lg border border-border p-4 space-y-2">
      {fileNameHeader}
      <FileTypeDetectorPanel
        initialFile={file}
        onImported={handleDetectorImported}
        onRemove={onRemove}
        startInPicker={startDetectorInPicker}
      />
      {skippedNotice}
    </div>
  );
}

/**
 * Treasurer-facing copy for rows dropped because their date lands in an
 * already-filed (locked) period. Mirrors the backend
 * <code>LockedPeriodImportCopy.Skipped</code> so the import
 * audit trail and this UI read the same. Singular/plural aware; no engineer enum
 * names, no "Schedule A"/federal framing (customers file their state).
 */
function skippedImportCopy(count: number): string {
  return count === 1
    ? "1 transaction dated in an already-filed period was skipped so it isn't reported "
      + 'twice. Add it by hand if you still need it.'
    : `${count} transactions dated in an already-filed period were skipped so they aren't `
      + 'reported twice. Add any you still need by hand.';
}

/**
 * Resolve the picked account into the commit's account-or-bucket fragment. The
 * no-account choice is an ALTERNATIVE to a real `bankAccountId`: it sends the
 * backend `useNoAccountBucket` signal (which find-or-creates the campaign system
 * bucket) and no account id. A real selection passes the id through unchanged.
 */
function accountOrBucket(
  bankAccountId: string,
): { useNoAccountBucket: true } | { bankAccountId: string } {
  return bankAccountId === NO_ACCOUNT_BUCKET
    ? { useNoAccountBucket: true }
    : { bankAccountId };
}

function isBankKind(kind: string): boolean {
  return kind === DetectedFileKind.BankCsv
    || kind === DetectedFileKind.BankXlsx
    || kind === DetectedFileKind.BankOfx;
}

function kindToFormat(kind: string): BankFileFormat {
  if (kind === DetectedFileKind.BankXlsx) return BankFileFormat.Xlsx;
  if (kind === DetectedFileKind.BankOfx) return BankFileFormat.Ofx;
  return BankFileFormat.Csv;
}

function formatError(e: unknown): string {
  if (e instanceof BankIngestErrorWithCode) return e.message;
  if (e instanceof Error) return e.message;
  return 'Operation failed.';
}
